import React, { useState, useCallback, useEffect, useMemo } from "react";
import IntlMessages from "util/IntlMessages";
import { getRole } from "../../../helpers";
import {
  Card,
  Table,
  Row,
  Col,
  Button,
  Tooltip,
  Tabs,
  Select,
  Space,
  Slider,
  InputNumber,
  Input,
  Result
} from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import SearchOutlinedIcon from "assets/images/icons/search_outlined.svg";
import SearchFilledIcon from "assets/images/icons/search_filled.svg";
import {
  useGetConsolidatedApiReportQuery,
  useGetConsolidatedFdfReportQuery,
  useGetConsolidatedDiagnosticsReportQuery,
  useDownloadConsolidatedApiReportMutation,
  useDownloadConsolidatedFdfReportMutation,
  useDownloadConsolidatedDiagnosticsReportMutation,
} from "../../../appRedux/api/salesReportApi";
import CustomPagination from "../../../components/CustomPagination";
import * as FileSaver from "file-saver";
import LoadingBar from "../../../components/LoadingBar";

const { Option } = Select;
const { TabPane } = Tabs;

const DEFAULT_PAGE_SIZE = 50;

// Custom Filter Dropdown Component
const CustomFilterDropdown = ({
  options,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder = "Search..."
}) => (
  <div style={{ padding: 8, width: 280 }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
      <Select
        mode="multiple"
        placeholder={placeholder}
        value={selectedKeys}
        onChange={setSelectedKeys}
        showSearch
        style={{
          width: '100%',
          minHeight: 20,
          marginBottom: 0,
          display: 'block'
        }}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        maxTagCount="responsive"
        allowClear={false}
        dropdownRender={menu => (
          <>
            {menu}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              padding: '0 8px'
            }}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm && confirm();
                }}
                style={{ flex: 1 }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => confirm && confirm()}
                style={{ flex: 1 }}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      >
        {options.map(option => (
          <Select.Option key={option.value} value={option.value}>
            {option.text}
          </Select.Option>
        ))}
      </Select>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <Button
        size="small"
        style={{ flex: 1 }}
        onClick={() => {
          clearFilters && clearFilters();
          confirm && confirm();
        }}
      >
        Reset
      </Button>
      <Button
        type="primary"
        size="small"
        style={{ flex: 1 }}
        onClick={() => confirm && confirm()}
      >
        Apply
      </Button>
    </div>
  </div>
);

const ConsolidatedSalesReport = () => {
  const isDiagnosticAdmin = getRole() === "DIAGNOSTIC_ADMIN" || getRole() === "DIAGNOSTIC_REVIEWER";
  const defaultTab = isDiagnosticAdmin ? "diagnostics" : "api";
  // State for active tab and pagination
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    if (isDiagnosticAdmin && activeTab !== "diagnostics") {
      setActiveTab("diagnostics");
    }
  }, [isDiagnosticAdmin, activeTab]);

  const [apiParams, setApiParams] = useState({
    offset: 0,
    limit: DEFAULT_PAGE_SIZE,
    current: 1,
    filters: {}
  });
  const [fdfParams, setFdfParams] = useState({
    offset: 0,
    limit: DEFAULT_PAGE_SIZE,
    current: 1,
    filters: {}
  });
  const [diagnosticsParams, setDiagnosticsParams] = useState({
    offset: 0,
    limit: DEFAULT_PAGE_SIZE,
    current: 1,
    filters: {}
  });

  // State to track current table filters (from Antd Table)
  const [apiTableFilters, setApiTableFilters] = useState({});
  const [fdfTableFilters, setFdfTableFilters] = useState({});
  const [diagnosticsTableFilters, setDiagnosticsTableFilters] = useState({});

  // API query parameters
  const apiQueryParams = {
    limit: apiParams.limit,
    offset: apiParams.offset,
    ...apiParams.filters
  };

  const fdfQueryParams = {
    limit: fdfParams.limit,
    offset: fdfParams.offset,
    ...fdfParams.filters
  };

  const diagnosticsQueryParams = {
    limit: diagnosticsParams.limit,
    offset: diagnosticsParams.offset,
    ...diagnosticsParams.filters
  };

  // API data fetching
  const {
    data: apiData = {},
    isLoading: isApiLoading,
    isFetching: isApiFetching,
    isError: isApiError,
    error: apiError,
    refetch: refetchApi
  } = useGetConsolidatedApiReportQuery(
    activeTab === "api" ? apiQueryParams : undefined,
    { skip: activeTab !== "api" }
  );

  const {
    data: fdfData = {},
    isLoading: isFdfLoading,
    isFetching: isFdfFetching,
    isError: isFdfError,
    error: fdfError,
    refetch: refetchFdf
  } = useGetConsolidatedFdfReportQuery(
    activeTab === "fdf" ? fdfQueryParams : undefined,
    { skip: activeTab !== "fdf" }
  );

  const {
    data: diagnosticsData = {},
    isLoading: isDiagnosticsLoading,
    isFetching: isDiagnosticsFetching,
    isError: isDiagnosticsError,
    error: diagnosticsError,
    refetch: refetchDiagnostics
  } = useGetConsolidatedDiagnosticsReportQuery(
    activeTab === "diagnostics" ? diagnosticsQueryParams : undefined,
    { skip: activeTab !== "diagnostics" }
  );

  // Download mutations
  const [downloadApiReport, { isLoading: isApiDownloading }] = useDownloadConsolidatedApiReportMutation();
  const [downloadFdfReport, { isLoading: isFdfDownloading }] = useDownloadConsolidatedFdfReportMutation();
  const [downloadDiagnosticsReport, { isLoading: isDiagnosticsDownloading }] = useDownloadConsolidatedDiagnosticsReportMutation();

  // Loading states
  const isApiTableLoading = isApiLoading || isApiFetching;
  const isFdfTableLoading = isFdfLoading || isFdfFetching;
  const isDiagnosticsTableLoading = isDiagnosticsLoading || isDiagnosticsFetching;
  const isDownloading = isApiDownloading || isFdfDownloading || isDiagnosticsDownloading;

  // Extract data from responses
  const apiRows = Array.isArray(apiData?.results) ? apiData.results : [];
  const apiTotal = apiData?.count || apiRows.length;
  const fdfRows = Array.isArray(fdfData?.results) ? fdfData.results : [];
  const fdfTotal = fdfData?.count || fdfRows.length;
  const diagnosticsRows = Array.isArray(diagnosticsData?.results) ? diagnosticsData.results : [];
  const diagnosticsTotal = diagnosticsData?.count || diagnosticsRows.length;

  // Filter options
  const apiFilterOptions = apiData?.filters || {};
  const fdfFilterOptions = fdfData?.filters || {};
  const diagnosticsFilterOptions = diagnosticsData?.filters || {};

  // Helper functions for filters
  const getSearchFilterProps = (dataIndex, filterData, fieldName, displayField, placeholder) => {
    const options = Array.isArray(filterData) ? filterData.map((item, idx) => ({
      text: item[displayField],
      value: item[fieldName],
      key: `${dataIndex}_${idx}`
    })) : [];

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <CustomFilterDropdown
          options={options}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          placeholder={`Select ${placeholder}`}
        />
      ),
      filterIcon: filtered => (
        filtered
          ? <img src={SearchFilledIcon} alt="Search Filled" style={{ width: 16, height: 16 }} />
          : <img src={SearchOutlinedIcon} alt="Search Outlined" style={{ width: 16, height: 16 }} />
      )
    };
  };

  const getRangeFilterProps = (dataIndex, minVal, maxVal, step = 1) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Slider
          range
          min={minVal}
          max={maxVal}
          step={step}
          defaultValue={[minVal, maxVal]}
          onChange={value => setSelectedKeys([value])}
          value={selectedKeys[0] || [minVal, maxVal]}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0' }}>
          <InputNumber
            min={minVal}
            max={maxVal}
            value={selectedKeys[0]?.[0] || minVal}
            onChange={value => setSelectedKeys([[value, selectedKeys[0]?.[1] || maxVal]])}
          />
          <InputNumber
            min={minVal}
            max={maxVal}
            value={selectedKeys[0]?.[1] || maxVal}
            onChange={value => setSelectedKeys([[selectedKeys[0]?.[0] || minVal, value]])}
          />
        </div>
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Filter
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) => {
      const recordValue = parseFloat(record[dataIndex]);
      return recordValue >= value[0] && recordValue <= value[1];
    }
  });

  const getTextFilterProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      filtered
        ? <img src={SearchFilledIcon} alt="Search Filled" style={{ width: 16, height: 16 }} />
        : <img src={SearchOutlinedIcon} alt="Search Outlined" style={{ width: 16, height: 16 }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
  });

  /** Strength → Comments — same fields as FDF tab (used for Diagnostic Admin on Diagnostics tab). */
  const buildSalesDetailColumns = (tableFilters, filterOptions) => [
    {
      title: "Strength",
      dataIndex: "strength",
      key: "strength",
      align: "center",
      width: 150,
      filteredValue: tableFilters.strength || null,
      ...getSearchFilterProps(
        "strength",
        filterOptions?.strength_filter,
        "strength",
        "strength",
        "Strength"
      )
    },
    {
      title: "Formulation Type",
      dataIndex: "formulation_md",
      key: "formulation_md",
      align: "center",
      width: 150,
      filteredValue: tableFilters.formulation_md || null,
      ...getSearchFilterProps(
        "formulation_md",
        filterOptions?.formulation_md_filter,
        "formulation_md",
        "formulation_md",
        "Formulation Type"
      )
    },
    {
      title: "Pack Size",
      dataIndex: "pack_size",
      key: "pack_size",
      align: "center",
      width: 100,
      filteredValue: tableFilters.pack_size || null,
      ...getSearchFilterProps(
        "pack_size",
        filterOptions?.pack_size_filter,
        "pack_size",
        "pack_size",
        "Pack Size"
      )
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      width: 100,
      filteredValue: tableFilters.quantity || null,
      ...getRangeFilterProps(
        "quantity",
        filterOptions?.quantity_filter?.quantity__min || 0,
        filterOptions?.quantity_filter?.quantity__max || 10000,
        1
      )
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      align: "center",
      width: 100,
      filteredValue: tableFilters.currency || null,
      ...getSearchFilterProps(
        "currency",
        filterOptions?.currency_filter,
        "currency",
        "currency",
        "Currency"
      )
    },
    {
      title: "Gross Sale Price (Local)",
      dataIndex: "gross_sale_price_currency",
      key: "gross_sale_price_currency",
      align: "center",
      width: 150,
      filteredValue: tableFilters.gross_sale_price_currency || null,
      ...getRangeFilterProps(
        "gross_sale_price_currency",
        0,
        1000,
        0.01
      )
    },
    {
      title: "Exchange Rate",
      dataIndex: "usd_exchange_rate",
      key: "usd_exchange_rate",
      align: "center",
      width: 150,
      filteredValue: tableFilters.usd_exchange_rate || null,
      ...getRangeFilterProps(
        "usd_exchange_rate",
        0,
        100,
        0.01
      )
    },
    {
      title: "Gross Sale Price (USD)",
      dataIndex: "gross_sale_price_usd",
      key: "gross_sale_price_usd",
      align: "center",
      width: 150,
      filteredValue: tableFilters.gross_sale_price_usd || null,
      ...getRangeFilterProps(
        "gross_sale_price_usd",
        filterOptions?.gross_sale_price_filter?.gross_sale_price_usd__min || 0,
        filterOptions?.gross_sale_price_filter?.gross_sale_price_usd__max || 1000,
        0.01
      )
    },
    {
      title: "Deductable Expenses",
      dataIndex: "deductable_expenses",
      key: "deductable_expenses",
      align: "center",
      width: 150,
      filteredValue: tableFilters.deductable_expenses || null,
      ...getRangeFilterProps(
        "deductable_expenses",
        0,
        100000,
        0.01
      )
    },
    {
      title: "Total Net Sales Value",
      dataIndex: "total_value",
      key: "total_value",
      align: "center",
      width: 150,
      filteredValue: tableFilters.total_value || null,
      ...getRangeFilterProps(
        "total_value",
        filterOptions?.net_sales_value_filter?.total_value__min || 0,
        filterOptions?.net_sales_value_filter?.total_value__max || 1000000,
        0.01
      )
    },
    {
      title: "Royalty %",
      dataIndex: "royalty_percent",
      key: "royalty_percent",
      align: "center",
      width: 100,
      filteredValue: tableFilters.royalty_percent || null,
      ...getRangeFilterProps(
        "royalty_percent",
        0,
        100,
        0.1
      )
    },
    {
      title: "Royalty Due",
      dataIndex: "royalty_due",
      key: "royalty_due",
      align: "center",
      width: 100,
      filteredValue: tableFilters.royalty_due || null,
      ...getRangeFilterProps(
        "royalty_due",
        filterOptions?.royalty_due_filter?.royalty_due__min || 0,
        filterOptions?.royalty_due_filter?.royalty_due__max || 100000,
        0.01
      )
    },
    {
      title: "Procurement Agency End-Country",
      dataIndex: "procurement_end_country",
      key: "procurement_end_country",
      align: "center",
      width: 150,
      filteredValue: tableFilters.procurement_end_country || null,
      ...getTextFilterProps("procurement_end_country")
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      align: "center",
      width: 200,
      filteredValue: tableFilters.comments || null,
      ...getTextFilterProps("comments")
    }
  ];

  // API columns configuration
  const apiColumns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      ellipsis: true,
      fixed: "left",
      align: "center",
      render: name => <span className="text-capitalize">{name}</span>,
      width: 250,
      filteredValue: apiTableFilters.company_name || null,
      ...getSearchFilterProps(
        "company_name",
        apiFilterOptions?.partner_filter,
        "partner_id",
        "company_name",
        "Company Name"
      )
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
      width: 150,
      filteredValue: apiTableFilters.year || null,
      ...getSearchFilterProps(
        "year",
        apiFilterOptions?.year_filter,
        "year",
        "year",
        "Year"
      )
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      align: "center",
      width: 150,
      filteredValue: apiTableFilters.month || null,
      ...getSearchFilterProps(
        "month",
        apiFilterOptions?.month_filter,
        "month",
        "month",
        "Month"
      )
    },
    {
      title: "Quarter",
      dataIndex: "quarter_name",
      key: "quarter_name",
      align: "center",
      width: 150,
      filteredValue: apiTableFilters.quarter_name || null,
      ...getSearchFilterProps(
        "quarter_name",
        apiFilterOptions?.quarter_filter,
        "quarter_name",
        "quarter_name",
        "Quarter"
      )
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      align: "center",
      width: 150,
      filteredValue: apiTableFilters.country_name || null,
      ...getSearchFilterProps(
        "country_name",
        apiFilterOptions?.country_filter,
        "country_id",
        "country_name",
        "Country"
      )
    },
    {
      title: "Purchaser",
      dataIndex: "purchaser",
      key: "purchaser",
      align: "center",
      render: name => <span className="text-capitalize">{name}</span>,
      width: 200,
      filteredValue: apiTableFilters.purchaser || null,
      ...getSearchFilterProps(
        "purchaser",
        apiFilterOptions?.purchaser_filter,
        "purchaser",
        "purchaser",
        "Purchaser"
      )
    },
    {
      title: "Api Name",
      dataIndex: "product_name",
      key: "product_name",
      align: "center",
      width: 200,
      filteredValue: apiTableFilters.product_name || null,
      ...getSearchFilterProps(
        "product_name",
        apiFilterOptions?.product_filter,
        "product_id",
        "product_name",
        "API Name"
      )
    },
    {
      title: "Quantity (Kg)",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      width: 150
    },
    {
      title: "Total Value (USD)",
      dataIndex: "total_value",
      key: "total_value",
      align: "center",
      width: 150
    }
  ];

  // Diagnostics columns: narrow set for non–Diagnostic Admin; full FDF-style detail columns for Diagnostic Admin
  const diagnosticsColumns = useMemo(() => {
    const base = [
      {
        title: "Company Name",
        dataIndex: "company_name",
        key: "company_name",
        ellipsis: true,
        fixed: "left",
        align: "center",
        render: name => <span className="text-capitalize">{name}</span>,
        width: 250,
        filteredValue: diagnosticsTableFilters.company_name || null,
        ...getSearchFilterProps(
          "company_name",
          diagnosticsFilterOptions?.partner_filter,
          "partner_id",
          "company_name",
          "Company Name"
        )
      },
      {
        title: "Year",
        dataIndex: "year",
        key: "year",
        align: "center",
        width: 150,
        filteredValue: diagnosticsTableFilters.year || null,
        ...getSearchFilterProps(
          "year",
          diagnosticsFilterOptions?.year_filter,
          "year",
          "year",
          "Year"
        )
      },
      {
        title: "Month",
        dataIndex: "month",
        key: "month",
        align: "center",
        width: 150,
        filteredValue: diagnosticsTableFilters.month || null,
        ...getSearchFilterProps(
          "month",
          diagnosticsFilterOptions?.month_filter,
          "month",
          "month",
          "Month"
        )
      },
      {
        title: "Quarter",
        dataIndex: "quarter_name",
        key: "quarter_name",
        align: "center",
        width: 150,
        filteredValue: diagnosticsTableFilters.quarter_name || null,
        ...getSearchFilterProps(
          "quarter_name",
          diagnosticsFilterOptions?.quarter_filter,
          "quarter_name",
          "quarter_name",
          "Quarter"
        )
      },
      {
        title: "Country",
        dataIndex: "country_name",
        key: "country_name",
        align: "center",
        width: 150,
        filteredValue: diagnosticsTableFilters.country_name || null,
        ...getSearchFilterProps(
          "country_name",
          diagnosticsFilterOptions?.country_filter,
          "country_id",
          "country_name",
          "Country"
        )
      },
      {
        title: "Product Name",
        dataIndex: "product_name",
        key: "product_name",
        align: "center",
        width: 200,
        filteredValue: diagnosticsTableFilters.product_name || null,
        ...getSearchFilterProps(
          "product_name",
          diagnosticsFilterOptions?.product_filter,
          "product_id",
          "product_name",
          "Product Name"
        )
      },
      {
        title: "Purchaser",
        dataIndex: "purchaser",
        key: "purchaser",
        align: "center",
        render: name => <span className="text-capitalize">{name}</span>,
        width: 200,
        filteredValue: diagnosticsTableFilters.purchaser || null,
        ...getSearchFilterProps(
          "purchaser",
          diagnosticsFilterOptions?.purchaser_filter,
          "purchaser",
          "purchaser",
          "Purchaser"
        )
      }
    ];
    if (!isDiagnosticAdmin) {
      return [
        ...base,
        {
          title: "Quantity (Kg)",
          dataIndex: "quantity",
          key: "quantity",
          align: "center",
          width: 150
        },
        {
          title: "Total Value (USD)",
          dataIndex: "total_value",
          key: "total_value",
          align: "center",
          width: 150
        }
      ];
    }
    return [...base, ...buildSalesDetailColumns(diagnosticsTableFilters, diagnosticsFilterOptions)];
  }, [isDiagnosticAdmin, diagnosticsTableFilters, diagnosticsFilterOptions]);

  // FDF columns configuration with all filters
  const fdfColumns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      ellipsis: true,
      fixed: "left",
      align: "center",
      render: name => <span className="text-capitalize">{name}</span>,
      width: 250,
      filteredValue: fdfTableFilters.company_name || null,
      ...getSearchFilterProps(
        "company_name",
        fdfFilterOptions?.partner_filter,
        "partner_id",
        "company_name",
        "Company Name"
      )
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
      width: 150,
      filteredValue: fdfTableFilters.year || null,
      ...getSearchFilterProps(
        "year",
        fdfFilterOptions?.year_filter,
        "year",
        "year",
        "Year"
      )
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      align: "center",
      width: 150,
      filteredValue: fdfTableFilters.month || null,
      ...getSearchFilterProps(
        "month",
        fdfFilterOptions?.month_filter,
        "month",
        "month",
        "Month"
      )
    },
    {
      title: "Quarter",
      dataIndex: "quarter_name",
      key: "quarter_name",
      align: "center",
      width: 150,
      filteredValue: fdfTableFilters.quarter_name || null,
      ...getSearchFilterProps(
        "quarter_name",
        fdfFilterOptions?.quarter_filter,
        "quarter_name",
        "quarter_name",
        "Quarter"
      )
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      align: "center",
      width: 150,
      filteredValue: fdfTableFilters.country_name || null,
      ...getSearchFilterProps(
        "country_name",
        fdfFilterOptions?.country_filter,
        "country_id",
        "country_name",
        "Country"
      )
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      align: "center",
      width: 200,
      filteredValue: fdfTableFilters.product_name || null,
      ...getSearchFilterProps(
        "product_name",
        fdfFilterOptions?.product_filter,
        "product_id",
        "product_name",
        "Product"
      )
    },
    {
      title: "Purchaser",
      dataIndex: "purchaser",
      key: "purchaser",
      align: "center",
      render: name => <span className="text-capitalize">{name}</span>,
      width: 200,
      filteredValue: fdfTableFilters.purchaser || null,
      ...getSearchFilterProps(
        "purchaser",
        fdfFilterOptions?.purchaser_filter,
        "purchaser",
        "purchaser",
        "Purchaser"
      )
    },
    ...buildSalesDetailColumns(fdfTableFilters, fdfFilterOptions)
  ];

  // Format filters for API payload
  const formatApiFilters = (filters) => {
    const formattedFilters = {};
    
    if (filters.company_name && filters.company_name.length > 0) {
      formattedFilters.partner_id = filters.company_name;
    }
    if (filters.year && filters.year.length > 0) {
      formattedFilters.year = filters.year;
    }
    if (filters.month && filters.month.length > 0) {
      formattedFilters.month = filters.month;
    }
    if (filters.quarter_name && filters.quarter_name.length > 0) {
      formattedFilters.quarter_id = filters.quarter_name;
    }
    if (filters.country_name && filters.country_name.length > 0) {
      formattedFilters.country_id = filters.country_name;
    }
    if (filters.purchaser && filters.purchaser.length > 0) {
      formattedFilters.purchaser = filters.purchaser;
    }
    if (filters.product_name && filters.product_name.length > 0) {
      formattedFilters.product_id = filters.product_name;
    }
    
    return formattedFilters;
  };

  // Format filters for FDF payload
  const formatFdfFilters = (filters) => {
    const formattedFilters = {};
    
    if (filters.company_name && filters.company_name.length > 0) {
      formattedFilters.partner_id = filters.company_name;
    }
    if (filters.year && filters.year.length > 0) {
      formattedFilters.year = filters.year;
    }
    if (filters.month && filters.month.length > 0) {
      formattedFilters.month = filters.month;
    }
    if (filters.quarter_name && filters.quarter_name.length > 0) {
      formattedFilters.quarter_id = filters.quarter_name;
    }
    if (filters.country_name && filters.country_name.length > 0) {
      formattedFilters.country_id = filters.country_name;
    }
    if (filters.product_name && filters.product_name.length > 0) {
      formattedFilters.product_id = filters.product_name;
    }
    if (filters.purchaser && filters.purchaser.length > 0) {
      formattedFilters.purchaser = filters.purchaser;
    }
    if (filters.strength && filters.strength.length > 0) {
      formattedFilters.strength = filters.strength;
    }
    if (filters.formulation_md && filters.formulation_md.length > 0) {
      formattedFilters.formulation_md = filters.formulation_md;
    }
    if (filters.pack_size && filters.pack_size.length > 0) {
      formattedFilters.pack_size = filters.pack_size;
    }
    if (filters.quantity && Array.isArray(filters.quantity) && filters.quantity.length === 2) {
      formattedFilters.quantity = {
        min_value: filters.quantity[0],
        max_value: filters.quantity[1]
      };
    }
    if (filters.currency && filters.currency.length > 0) {
      formattedFilters.currency = filters.currency;
    }
    if (filters.gross_sale_price_currency && Array.isArray(filters.gross_sale_price_currency) && filters.gross_sale_price_currency.length === 2) {
      formattedFilters.gross_sale_price = {
        min_value: filters.gross_sale_price_currency[0],
        max_value: filters.gross_sale_price_currency[1]
      };
    }
    if (filters.gross_sale_price_usd && Array.isArray(filters.gross_sale_price_usd) && filters.gross_sale_price_usd.length === 2) {
      formattedFilters.gross_sale_price = {
        min_value: filters.gross_sale_price_usd[0],
        max_value: filters.gross_sale_price_usd[1]
      };
    }
    if (filters.deductable_expenses && Array.isArray(filters.deductable_expenses) && filters.deductable_expenses.length === 2) {
      formattedFilters.deductable_expenses = {
        min_value: filters.deductable_expenses[0],
        max_value: filters.deductable_expenses[1]
      };
    }
    if (filters.total_value && Array.isArray(filters.total_value) && filters.total_value.length === 2) {
      formattedFilters.net_sales_value = {
        min_value: filters.total_value[0],
        max_value: filters.total_value[1]
      };
    }
    if (filters.royalty_percent && Array.isArray(filters.royalty_percent) && filters.royalty_percent.length === 2) {
      formattedFilters.royalty_percent = {
        min_value: filters.royalty_percent[0],
        max_value: filters.royalty_percent[1]
      };
    }
    if (filters.royalty_due && Array.isArray(filters.royalty_due) && filters.royalty_due.length === 2) {
      formattedFilters.royalty_due = {
        min_value: filters.royalty_due[0],
        max_value: filters.royalty_due[1]
      };
    }
    if (filters.procurement_end_country && filters.procurement_end_country.length > 0) {
      formattedFilters.procurement_end_country = filters.procurement_end_country;
    }
    if (filters.comments && filters.comments.length > 0) {
      formattedFilters.comments = filters.comments;
    }
    
    return formattedFilters;
  };

  // Format filters for Diagnostics payload (Diagnostic Admin: same extended filters as FDF)
  const formatDiagnosticsFilters = (filters) => {
    if (isDiagnosticAdmin) {
      return formatFdfFilters(filters);
    }
    return formatApiFilters(filters);
  };

  // Table change handlers
  const handleApiTableChange = useCallback((pagination = {}, filters) => {
    const current = pagination.current || 1;
    const pageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;
    const offset = (current - 1) * pageSize;

    // Merge new filters with existing ones
    const mergedFilters = { ...apiTableFilters, ...filters };
    setApiTableFilters(mergedFilters);

    const formattedFilters = formatApiFilters(mergedFilters);

    setApiParams(prev => ({
      ...prev,
      offset: offset,
      limit: pageSize,
      current: current,
      filters: formattedFilters
    }));
  }, [apiTableFilters]);

  const handleFdfTableChange = useCallback((pagination = {}, filters) => {
    const current = pagination.current || 1;
    const pageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;
    const offset = (current - 1) * pageSize;

    // Merge new filters with existing ones
    const mergedFilters = { ...fdfTableFilters, ...filters };
    setFdfTableFilters(mergedFilters);

    const formattedFilters = formatFdfFilters(mergedFilters);

    setFdfParams(prev => ({
      ...prev,
      offset: offset,
      limit: pageSize,
      current: current,
      filters: formattedFilters
    }));
  }, [fdfTableFilters]);

  const handleDiagnosticsTableChange = useCallback((pagination = {}, filters) => {
    const current = pagination.current || 1;
    const pageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;
    const offset = (current - 1) * pageSize;

    const mergedFilters = { ...diagnosticsTableFilters, ...filters };
    setDiagnosticsTableFilters(mergedFilters);

    const formattedFilters = formatDiagnosticsFilters(mergedFilters);

    setDiagnosticsParams(prev => ({
      ...prev,
      offset: offset,
      limit: pageSize,
      current: current,
      filters: formattedFilters
    }));
  }, [diagnosticsTableFilters, isDiagnosticAdmin]);

  // Tab change handler
  const handleTabChange = useCallback((newActiveTab) => {
    setActiveTab(newActiveTab);
    // Reset pagination when switching tabs
    if (newActiveTab === "api") {
      setApiParams(prev => ({ ...prev, current: 1, offset: 0 }));
    } else if (newActiveTab === "fdf") {
      setFdfParams(prev => ({ ...prev, current: 1, offset: 0 }));
    } else if (newActiveTab === "diagnostics") {
      setDiagnosticsParams(prev => ({ ...prev, current: 1, offset: 0 }));
    }
  }, []);

  // Download handler
  const handleDownload = useCallback(async (filename) => {
    try {
      if (activeTab === "api") {
        const result = await downloadApiReport({
          limit: apiParams.limit,
          offset: apiParams.offset,
          ...apiParams.filters
        }).unwrap();
        FileSaver.saveAs(result, filename);
      } else if (activeTab === "fdf") {
        const result = await downloadFdfReport({
          limit: fdfParams.limit,
          offset: fdfParams.offset,
          ...fdfParams.filters
        }).unwrap();
        FileSaver.saveAs(result, filename);
      } else if (activeTab === "diagnostics") {
        const result = await downloadDiagnosticsReport({
          limit: diagnosticsParams.limit,
          offset: diagnosticsParams.offset,
          ...diagnosticsParams.filters
        }).unwrap();
        FileSaver.saveAs(result, filename);
      }
    } catch (error) {
      console.error("Download failed:", error);
    }
  }, [activeTab, apiParams, fdfParams, diagnosticsParams, downloadApiReport, downloadFdfReport, downloadDiagnosticsReport]);

  // Show loading state — only block the page on the very first load (no data yet).
  // Subsequent refetches show the inline table spinner so the loader doesn't get stuck.
  if ((activeTab === "api" && isApiLoading) ||
    (activeTab === "fdf" && isFdfLoading) ||
    (activeTab === "diagnostics" && isDiagnosticsLoading)) {
    return <LoadingBar loading={true} />;
  }

  // Show error state with retry when the active tab's query has failed.
  const activeError =
    activeTab === "api" ? (isApiError ? apiError : null) :
    activeTab === "fdf" ? (isFdfError ? fdfError : null) :
    activeTab === "diagnostics" ? (isDiagnosticsError ? diagnosticsError : null) :
    null;
  if (activeError) {
    const message =
      activeError?.status === "TIMEOUT_ERROR"
        ? "Request timed out. Please try again."
        : activeError?.data?.message || activeError?.error || "Failed to load Sales report.";
    const tabLabel =
      activeTab === "api" ? "API" :
      activeTab === "fdf" ? "FDF" :
      "Diagnostics";
    const retry =
      activeTab === "api" ? refetchApi :
      activeTab === "fdf" ? refetchFdf :
      refetchDiagnostics;
    return (
      <Result
        status="error"
        title={`Failed to load ${tabLabel} sales report`}
        subTitle={message}
        extra={
          <Button type="primary" onClick={() => retry()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Row>
        <Col span={24}>
          <div className="gx-flex-row gx-align-items-center gx-justify-content-between">
            <h1 className="title gx-mb-4">
              <IntlMessages id="report.sales-report" />
            </h1>
          </div>
        </Col>
        <Col span={24}>
          <Card className="mb-0">
            <Tabs
              tabPosition="top"
              type="card"
              className="sales-report-tabs"
              activeKey={activeTab}
              tabBarExtraContent={
                <div>
                  <Tooltip
                    title={`download ${activeTab === "api" ? "API" : activeTab === "diagnostics" ? "Diagnostics" : "FDF"} report!`}
                  >
                    <Button
                      className="mb-0"
                      id="report-download"
                      onClick={() => handleDownload(`${activeTab}-sales-report.csv`)}
                      loading={isDownloading}
                    >
                      <DownloadOutlined className="font-20" />
                    </Button>
                  </Tooltip>
                </div>
              }
              onChange={handleTabChange}
            >
              {!isDiagnosticAdmin && (
                <TabPane tab="API" key="api">
                  <Table
                    className="gx-table-responsive mpp-list-table"
                    columns={apiColumns}
                    dataSource={apiRows}
                    pagination={false}
                    size="small"
                    onChange={handleApiTableChange}
                    rowKey={(record) => record.id || record.Id || Math.random().toString()}
                    bordered
                    scroll={{ x: 1500, y: 'calc(100vh - 350px)' }}
                    sticky={{ offsetHeader: 0 }}
                    loading={isApiTableLoading}
                  />

                  <CustomPagination
                    current={apiParams.current}
                    total={apiTotal}
                    pageSize={apiParams.limit}
                    onChange={(page, pageSize) => {
                      const offset = (page - 1) * pageSize;
                      setApiParams(prev => ({
                        ...prev,
                        offset: offset,
                        limit: pageSize,
                        current: page,
                      }));
                    }}
                    showSizeChanger={true}
                    pageSizeOptions={['10', '20', '50', '100']}
                  />
                </TabPane>
              )}
              {!isDiagnosticAdmin && (
                <TabPane tab="FDF" key="fdf">
                  <Table
                    className="gx-table-responsive mpp-list-table"
                    columns={fdfColumns}
                    dataSource={fdfRows}
                    pagination={false}
                    size="small"
                    onChange={handleFdfTableChange}
                    rowKey={(record) => record.id || record.Id || Math.random().toString()}
                    bordered
                    scroll={{ x: 2500, y: 'calc(100vh - 350px)' }}
                    sticky={{ offsetHeader: 0 }}
                    loading={isFdfTableLoading}
                  />

                  <CustomPagination
                    current={fdfParams.current}
                    total={fdfTotal}
                    pageSize={fdfParams.limit}
                    onChange={(page, pageSize) => {
                      const offset = (page - 1) * pageSize;
                      setFdfParams(prev => ({
                        ...prev,
                        offset: offset,
                        limit: pageSize,
                        current: page,
                      }));
                    }}
                    showSizeChanger={true}
                    pageSizeOptions={['10', '20', '50', '100']}
                  />
                </TabPane>
              )}
              <TabPane tab="Diagnostics" key="diagnostics">
                <Table
                  className="gx-table-responsive mpp-list-table"
                  columns={diagnosticsColumns}
                  dataSource={diagnosticsRows}
                  pagination={false}
                  size="small"
                  onChange={handleDiagnosticsTableChange}
                  rowKey={(record) => record.id || record.Id || Math.random().toString()}
                  bordered
                  scroll={{ x: isDiagnosticAdmin ? 2500 : 1700, y: 'calc(100vh - 350px)' }}
                  sticky={{ offsetHeader: 0 }}
                  loading={isDiagnosticsTableLoading}
                />

                <CustomPagination
                  current={diagnosticsParams.current}
                  total={diagnosticsTotal}
                  pageSize={diagnosticsParams.limit}
                  onChange={(page, pageSize) => {
                    const offset = (page - 1) * pageSize;
                    setDiagnosticsParams(prev => ({
                      ...prev,
                      offset: offset,
                      limit: pageSize,
                      current: page,
                    }));
                  }}
                  showSizeChanger={true}
                  pageSizeOptions={['10', '20', '50', '100']}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ConsolidatedSalesReport;